import React, { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';

const Lightbox = ({ image, index, total, onClose, onNext, onPrev }) => {
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    if (!image) return;

    setProgress(100);
    const startTime = Date.now();
    const duration = 5000;

    const progressTimer = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, 100 - (elapsed / duration) * 100);
      setProgress(remaining);
      
      if (elapsed >= duration) {
        onNext();
      }
    }, 50);

    return () => clearInterval(progressTimer);
  }, [image, onNext]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight') onNext();
      if (e.key === 'ArrowLeft') onPrev();
      if (e.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onNext, onPrev, onClose]);

  if (!image) return null;

  return (
    <div className="fixed inset-0 z-[100] lightbox-backdrop flex flex-col items-center justify-center p-4 md:p-8" onClick={onClose}>
      {/* Header Controls */}
      <div className="absolute top-0 left-0 right-0 p-6 flex items-center justify-between z-[110]">
        <div className="glass-button px-4 py-2 rounded-full text-white text-sm font-medium">
          {index + 1} <span className="opacity-50 mx-1">/</span> {total}
        </div>
        
        <button 
          className="glass-button w-12 h-12 rounded-full flex items-center justify-center text-white"
          onClick={onClose}
        >
          <X size={24} />
        </button>
      </div>

      {/* Main Content Area */}
      <div className="relative w-full max-w-6xl flex items-center justify-center gap-4" onClick={(e) => e.stopPropagation()}>
        {/* Previous Button - Hidden on very small screens, visible on md+ */}
        <button 
          className="hidden md:flex glass-button w-14 h-14 rounded-full items-center justify-center text-white flex-shrink-0"
          onClick={onPrev}
        >
          <ChevronLeft size={32} />
        </button>

        {/* Image Container */}
        <div className="relative group w-full flex flex-col items-center">
          <div className="relative overflow-hidden rounded-3xl shadow-2xl border border-white/10 bg-black/20">
            <img 
              src={image.url} 
              alt={image.title} 
              className="w-full max-h-[70vh] object-contain transition-transform duration-700 group-hover:scale-105" 
            />
            
            {/* Mobile Navigation Overlays - Left/Right invisible click zones */}
            <div className="absolute inset-y-0 left-0 w-1/4 md:hidden" onClick={onPrev}></div>
            <div className="absolute inset-y-0 right-0 w-1/4 md:hidden" onClick={onNext}></div>

            {/* Mobile Arrows - More prominent on mobile */}
            <div className="md:hidden absolute inset-x-0 bottom-1/2 translate-y-1/2 flex justify-between px-2 pointer-events-none">
              <div className="glass-button w-12 h-12 rounded-full flex items-center justify-center text-white pointer-events-auto shadow-lg" onClick={onPrev}>
                <ChevronLeft size={24} />
              </div>
              <div className="glass-button w-12 h-12 rounded-full flex items-center justify-center text-white pointer-events-auto shadow-lg" onClick={onNext}>
                <ChevronRight size={24} />
              </div>
            </div>

            {/* Progress Bar */}
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/10">
              <div 
                className="h-full bg-emerald-400 transition-all duration-75 ease-linear"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          </div>

          {/* Caption */}
          <div className="mt-8 text-center px-4 max-w-2xl">
            <h3 className="text-3xl md:text-4xl font-bold text-white mb-3 tracking-tight">
              {image.title}
            </h3>
            {image.desc && (
              <p className="text-emerald-200/80 text-lg font-poppins leading-relaxed">
                {image.desc}
              </p>
            )}
          </div>
        </div>

        {/* Next Button - Hidden on very small screens, visible on md+ */}
        <button 
          className="hidden md:flex glass-button w-14 h-14 rounded-full items-center justify-center text-white flex-shrink-0"
          onClick={onNext}
        >
          <ChevronRight size={32} />
        </button>
      </div>

      {/* Keyboard Hint */}
      <div className="absolute bottom-6 hidden md:block text-white/30 text-xs font-medium uppercase tracking-widest">
        Use arrow keys to navigate
      </div>
    </div>
  );
};

export default Lightbox;
